import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import MultiSelect, {
  MultiSelectOption,
} from "../../../components/searchable-multi-select";
import Table, { TableColumns } from "../../../components/table";
import Tabs, { TabItem } from "../../../components/tabs";
import { RoutePaths } from "../../../models";
import { getPolicyAttachmentCreationOptions } from "../../../actions/policy-attachment.action";
import {
  useCreateInputMutation,
  useCreateRunMutation,
  useLazyGetInputDownloadUrlQuery,
  useListInputsQuery,
  useListRunsQuery,
} from "../../../actions/policy-simulation.action";
import {
  PolicySimulationInput,
  PolicySimulationInputDownloadResponse,
  PolicySimulationPolicyOption,
  PolicySimulationProgress,
  PolicySimulationRunRequest,
} from "../../../models/policySimulation";
import { PolicyAttachmentCreationOptions } from "../../../models/underwritingPolicyAttachment";
import dispatch from "../../../middleware";
import {
  parseNonEmptyObject,
  parseObject,
  parseOptionalPositiveInteger,
} from "./validation";
import useScreenName from "../../../hooks/useScreenName";

const DEFAULT_TABLE = "risk_analytics_data.underwriting_policy_execution_logs";
const initialInput = {
  table: DEFAULT_TABLE,
  filters: "{}",
  limit: "",
  description: "",
};
const initialRun = {
  mode: "ALL_STEPS",
  url: "",
  table: DEFAULT_TABLE,
  filters: "{}",
  limit: "",
  overrides: "{}",
  policyNames: [] as string[],
  description: "",
  csvInclude: "",
  csvExclude: "",
};

function message(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

function progressLabel(progress?: PolicySimulationProgress | null) {
  if (!progress) return "-";
  const rows =
    progress.processed_rows === undefined
      ? ""
      : ` (${progress.processed_rows} rows)`;
  return `${progress.step}: ${progress.status}${rows}`;
}

type SimulationTab = "inputs" | "runs";


export default function PolicySimulationsPage() {
  useScreenName("Credit Decision Policy Simulations");
  const storeDispatch = useDispatch();
  const [tab, setTab] = useState<SimulationTab>("inputs");
  const [selectedInput, setSelectedInput] =
    useState<PolicySimulationInput | null>(null);
  const [signedInputUrl, setSignedInputUrl] = useState("");
  const [policies, setPolicies] = useState<PolicySimulationPolicyOption[]>([]);
  const [inputForm, setInputForm] = useState(initialInput);
  const [runForm, setRunForm] = useState(initialRun);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputQuery = useListInputsQuery(undefined, { skip: tab !== "inputs" });
  const runQuery = useListRunsQuery(undefined, { skip: tab !== "runs" });
  const inputs = inputQuery.data || [];
  const runs = runQuery.data || [];
  const [createInput] = useCreateInputMutation();
  const [createRun] = useCreateRunMutation();
  const [triggerDownloadUrl] = useLazyGetInputDownloadUrlQuery();



  useEffect(() => {
    if (tab !== "runs") return;
    dispatch(storeDispatch, getPolicyAttachmentCreationOptions())
      .then((data: unknown) => {
        const options = data as PolicyAttachmentCreationOptions;
        setPolicies(
          (options.availablePolicies || []) as PolicySimulationPolicyOption[],
        );
      })
      .catch(() => undefined);
  }, [storeDispatch, tab]);

  async function generateInput() {
    setError("");
    setSubmitting(true);
    try {
      await createInput({
        table: inputForm.table,
        filters: parseNonEmptyObject(inputForm.filters, "Filters"),
        limit: parseOptionalPositiveInteger(inputForm.limit, "Limit"),
        description: inputForm.description || undefined,
      }).unwrap();
      setInputForm(initialInput);
      inputQuery.refetch();
    } catch (e) {
      setError(message(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function createSimulationRun() {
    setError("");
    setSubmitting(true);
    try {
      const overrideSpec = parseObject(runForm.overrides, "Override JSON");
      if (runForm.policyNames.length) {
        overrideSpec.policy_names = runForm.policyNames;
      }
      const csvIncludeFields = runForm.csvInclude
        .split(/[,\n]/)
        .map((field) => field.trim())
        .filter(Boolean);
      const csvExcludeFields = runForm.csvExclude
        .split(/[,\n]/)
        .map((field) => field.trim())
        .filter(Boolean);
      const commonFields = {
        description: runForm.description || undefined,
        csv_include_fields: csvIncludeFields.length ? csvIncludeFields : undefined,
        csv_exclude_fields: csvExcludeFields.length ? csvExcludeFields : undefined,
      };
      const body: PolicySimulationRunRequest =
        runForm.mode === "ALL_STEPS"
          ? {
              mode: "ALL_STEPS",
              fetch: {
                table: runForm.table,
                filters: parseNonEmptyObject(runForm.filters, "Fetch filters"),
                limit: parseOptionalPositiveInteger(
                  runForm.limit,
                  "Fetch limit",
                ),
              },
              override_spec: overrideSpec,
              ...commonFields,
            }
          : {
              mode: "FROM_INPUT",
              input: { type: "SIGNED_URL", url: runForm.url },
              override_spec: overrideSpec,
              ...commonFields,
            };
      await createRun(body).unwrap();
      setRunForm(initialRun);
      runQuery.refetch();
    } catch (e) {
      setError(message(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function getInputUrl(inputId: string, download = false) {
    try {
      const result = await triggerDownloadUrl(inputId);
      const response = result.data as
        | PolicySimulationInputDownloadResponse
        | undefined;
      if (!response?.url) throw new Error("Input URL is not available");
      if (download) window.location.assign(response.url);
      else setSignedInputUrl(response.url);
    } catch (e) {
      setError(message(e));
    }
  }

  function showInput(inputId: string) {
    setSelectedInput(inputs.find((input) => input.input_id === inputId) || null);
  }

  const inputColumns: TableColumns[] = [
    { field: "input_id", label: "Input ID" },
    { field: "created_at", label: "Created" },
    { field: "description", label: "Description" },
    { field: "actor_email", label: "Actor" },
    { field: "status", label: "Status" },
    { field: "actions", label: "Actions" },
  ];
  const runColumns: TableColumns[] = [
    { field: "run_link", label: "Run ID" },
    { field: "created_at", label: "Created" },
    { field: "description", label: "Description" },
    { field: "actor_email", label: "Actor" },
    { field: "progress", label: "Progress" },
    { field: "mode", label: "Mode" },
    { field: "status", label: "Status" },
  ];
  const inputRows = inputs.map((input) => ({
    ...input,
    description: input.description || "-",
    actor_email: input.actor_email || "-",
    created_at: input.created_at
      ? new Date(input.created_at).toLocaleString()
      : "-",
    actions: (
      <div className="d-flex flex-wrap" style={{ gap: 8 }}>
        <Button variant="blue-text" onClick={() => showInput(input.input_id)}>
          Details
        </Button>
        <Button
          variant="blue-text"
          onClick={() => getInputUrl(input.input_id, true)}
          disabled={input.status !== "SUCCEEDED"}
        >
          JSONL
        </Button>
        <Button
          variant="blue-text"
          onClick={() => getInputUrl(input.input_id)}
          disabled={input.status !== "SUCCEEDED"}
        >
          Get signed URL
        </Button>
      </div>
    ),
  }));
  const runRows = runs.map((run) => ({
    ...run,
    description: run.description || "-",
    actor_email: run.actor_email || "-",
    progress: progressLabel(run.progress),
    run_link: (
      <Link
        to={RoutePaths.CREDIT_DECISION_POLICY_SIMULATION_DETAIL.replace(
          ":runId",
          encodeURIComponent(run.run_id),
        )}
      >
        {run.run_id}
      </Link>
    ),
    created_at: run.created_at
      ? new Date(run.created_at).toLocaleString()
      : "-",
  }));
  const policyOptions: MultiSelectOption[] = policies.map((policy) => ({
    value: policy.name,
    text: policy.name,
  }));
  const tabs: TabItem<SimulationTab>[] = [
    { label: "Inputs", value: "inputs" },
    { label: "Simulation Run", value: "runs" },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Policy Simulations</h2>
        <Button
          variant="blue-outline"
          onClick={() => {
            if (tab === "inputs") inputQuery.refetch();
            else runQuery.refetch();
          }}
        >
          Refresh
        </Button>
      </div>
      {error && <Alert type="danger" message={error} />}
      <div className="dashboard-container p-3 mb-4">
        <Tabs items={tabs} value={tab} onChange={setTab} />
      </div>

      {tab === "inputs" && (
        <>
          <div className="dashboard-container p-4 mb-4">
            <h5 className="mb-4">Generate input</h5>
            <div className="row">
              <div className="col-md-8 form-group">
                <label className="form-label">Source table</label>
                <input
                  className="form-control"
                  value={inputForm.table}
                  onChange={(e) =>
                    setInputForm({ ...inputForm, table: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4 form-group">
                <label className="form-label">Limit (optional)</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  value={inputForm.limit}
                  onChange={(e) =>
                    setInputForm({ ...inputForm, limit: e.target.value })
                  }
                />
              </div>
              <div className="col-12 form-group">
                <label className="form-label">Filters JSON</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={inputForm.filters}
                  onChange={(e) =>
                    setInputForm({ ...inputForm, filters: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 form-group">
                <label className="form-label">Description (optional)</label>
                <input
                  className="form-control"
                  maxLength={500}
                  value={inputForm.description}
                  onChange={(e) =>
                    setInputForm({ ...inputForm, description: e.target.value })
                  }
                />
              </div>
            </div>
            <Button loading={submitting} onClick={generateInput}>
              Generate input
            </Button>
          </div>
          <div className="dashboard-container p-4">
            <h5 className="mb-4">Inputs</h5>
            <Table
              loading={inputQuery.isLoading}
              columns={inputColumns}
              data={inputRows}
              keyField="input_id"
            />
            {signedInputUrl && (
              <div className="form-group mt-4">
                <label className="form-label">Signed input URL</label>
                <input
                  className="form-control"
                  readOnly
                  value={signedInputUrl}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </div>
            )}
            {selectedInput && (
              <pre className="small bg-light p-3 mt-4">
                {JSON.stringify(selectedInput, null, 2)}
              </pre>
            )}
          </div>
        </>
      )}

      {tab === "runs" && (
        <>
          <div className="dashboard-container p-4 mb-4">
            <h5 className="mb-4">New run</h5>
            <div className="row">
              <div className="col-md-4 form-group">
                <label className="form-label">Mode</label>
                <select
                  className="form-control"
                  value={runForm.mode}
                  onChange={(e) =>
                    setRunForm({
                      ...runForm,
                      mode: e.target.value as "ALL_STEPS" | "FROM_INPUT",
                    })
                  }
                >
                  <option value="ALL_STEPS">ALL_STEPS</option>
                  <option value="FROM_INPUT">FROM_INPUT</option>
                </select>
              </div>
              {runForm.mode === "FROM_INPUT" && (
                <div className="col-md-8 form-group">
                  <label className="form-label">Signed input URL</label>
                  <input
                    className="form-control"
                    value={runForm.url}
                    onChange={(e) =>
                      setRunForm({ ...runForm, url: e.target.value })
                    }
                  />
                </div>
              )}
            </div>
            {runForm.mode === "ALL_STEPS" && (
              <div className="row">
                <div className="col-md-8 form-group">
                  <label className="form-label">Source table</label>
                  <input
                    className="form-control"
                    value={runForm.table}
                    onChange={(e) =>
                      setRunForm({ ...runForm, table: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4 form-group">
                  <label className="form-label">Limit (optional)</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    value={runForm.limit}
                    onChange={(e) =>
                      setRunForm({ ...runForm, limit: e.target.value })
                    }
                  />
                </div>
                <div className="col-12 form-group">
                  <label className="form-label">Fetch filters JSON</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={runForm.filters}
                    onChange={(e) =>
                      setRunForm({ ...runForm, filters: e.target.value })
                    }
                  />
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-md-6 form-group">
                <label className="form-label">Description (optional)</label>
                <input
                  className="form-control"
                  maxLength={500}
                  value={runForm.description}
                  onChange={(e) =>
                    setRunForm({ ...runForm, description: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 form-group">
                <label className="form-label">CSV include fields</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="One field per line or comma-separated"
                  value={runForm.csvInclude}
                  onChange={(e) =>
                    setRunForm({ ...runForm, csvInclude: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 form-group">
                <label className="form-label">CSV exclude fields</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="One field per line or comma-separated"
                  value={runForm.csvExclude}
                  onChange={(e) =>
                    setRunForm({ ...runForm, csvExclude: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Override JSON</label>
              <textarea
                className="form-control"
                rows={3}
                value={runForm.overrides}
                onChange={(e) =>
                  setRunForm({ ...runForm, overrides: e.target.value })
                }
              />
            </div>
            {policies.length > 0 && (
              <div className="form-group">
                <label className="form-label">Policy names (optional)</label>
                <MultiSelect
                  options={policyOptions}
                  placeholder="Select policies"
                  value={runForm.policyNames}
                  onChange={(values) =>
                    setRunForm({
                      ...runForm,
                      policyNames: values.map(String),
                    })
                  }
                  showBadges
                />
              </div>
            )}
            <Button loading={submitting} onClick={createSimulationRun}>
              Create run
            </Button>
          </div>
          <div className="dashboard-container p-4">
            <h5 className="mb-4">Runs</h5>
            <Table
              loading={runQuery.isLoading}
              columns={runColumns}
              data={runRows}
              keyField="run_id"
            />
          </div>
        </>
      )}
    </div>
  );
}
